import type { NavePlanta } from '../../api';

/**
 * La matemática del editor de planta: ajustar a la grilla, resolver el
 * rectángulo de un trazo, enganchar al borde de otra nave y decidir en qué
 * nave cayó un punto. Vive aparte del componente porque es la parte que
 * conviene poder probar sin montar nada.
 */

/** Las medidas se redondean a medio metro al arrastrar. */
export const PASO_M = 0.5;

export const ajustar = (v: number) => Math.round(v / PASO_M) * PASO_M;

/** Rectángulo del trazo, ya ajustado y ordenado. */
export function rectanguloDelTrazo(t: { x0: number; y0: number; x1: number; y1: number }) {
  return {
    x: ajustar(Math.min(t.x0, t.x1)),
    y: ajustar(Math.min(t.y0, t.y1)),
    ancho: ajustar(Math.abs(t.x1 - t.x0)),
    largo: ajustar(Math.abs(t.y1 - t.y0)),
  };
}

export type Punto = { x: number; y: number };

/**
 * Segmento de una pared, ajustado a la grilla y enderezado a horizontal o
 * vertical: una pared en diagonal casi nunca es lo que el operario quiso, son
 * tabiques paralelos a las paredes de la nave. Primero se ajustan los dos
 * puntos por separado, y recién después se compara qué eje recorrió más para
 * decidir cuál se aplana — por eso el eje aplanado toma el valor de `a` ya
 * ajustado, no el original. Con `|dx| >= |dy|` gana el horizontal, para que
 * el empate a 45° sea determinista y la pared no salte mientras se la dibuja.
 */
export function ajustarASegmento(a: Punto, b: Punto): { a: Punto; b: Punto } {
  const ap = { x: ajustar(a.x), y: ajustar(a.y) };
  const bp = { x: ajustar(b.x), y: ajustar(b.y) };
  const dx = bp.x - ap.x;
  const dy = bp.y - ap.y;
  return Math.abs(dx) >= Math.abs(dy)
    ? { a: ap, b: { x: bp.x, y: ap.y } }
    : { a: ap, b: { x: ap.x, y: bp.y } };
}

/**
 * Pega el valor al borde más cercano si está a tiro. Es lo que hace que dos
 * naves queden pegadas de verdad y no separadas por diez centímetros que
 * después aparecen como una ranura en el 3D.
 */
export function engancharABordes(v: number, bordes: readonly number[], tolerancia = 0.75): number {
  let mejor = v;
  let distancia = tolerancia;
  for (const b of bordes) {
    const d = Math.abs(v - b);
    if (d <= distancia) {
      distancia = d;
      mejor = b;
    }
  }
  return mejor;
}

/**
 * Engancha un tramo —una nave, que arranca en `v` y mide `medida`— contra los
 * bordes de las demás. Se prueban los dos lados, porque una nave se puede
 * apoyar contra la pared de al lado por su izquierda o por su derecha, y gana
 * el que menos la mueve.
 *
 * Ojo con lo que parece un detalle y es el bug que ya tuvo dos veces: para
 * saber si un lado enganchó hay que comparar cada lado **contra su propia
 * entrada**, nunca el resultado de ida y vuelta contra `v`. `engancharABordes`
 * devuelve el valor sin tocar cuando no hay borde a tiro, y con medidas que no
 * caen en la grilla —una nave de 20.2 m— `v + medida - medida !== v` por el
 * redondeo del flotante. Comparando así, el lado que NO enganchó entra igual
 * con un movimiento de 1e-14 y le gana al que sí enganchó, que es justo lo que
 * dejaba la ranura entre las dos naves.
 */
export function engancharTramoABordes(
  v: number,
  medida: number,
  bordes: readonly number[],
  tolerancia = 0.75,
): number {
  const ini = engancharABordes(v, bordes, tolerancia);
  const fin = engancharABordes(v + medida, bordes, tolerancia);
  const engancharon = [
    ...(ini !== v ? [ini] : []),
    ...(fin !== v + medida ? [fin - medida] : []),
  ];
  if (engancharon.length === 0) return v;
  return engancharon.reduce((a, b) => (Math.abs(a - v) <= Math.abs(b - v) ? a : b));
}

/**
 * En qué nave cayó un punto del predio. El borde compartido cuenta para la
 * nave que arranca ahí: apoyar algo contra la pared del medio tiene que
 * quedar del lado que se está mirando, no del de al lado.
 */
export function naveEnElPunto(p: { x: number; y: number }, naves: readonly NavePlanta[]): NavePlanta | null {
  const candidatas = naves.filter(
    (n) => p.x >= n.x && p.x <= n.x + n.ancho && p.y >= n.y && p.y <= n.y + n.largo,
  );
  if (candidatas.length === 0) return null;
  // Con el punto justo sobre el borde entran dos: gana la de origen más a la
  // derecha y más abajo, que es la que empieza en ese borde.
  return candidatas.reduce((a, b) => (b.x > a.x || (b.x === a.x && b.y > a.y) ? b : a));
}

/** Pasa un punto del predio a coordenadas de una nave. */
export const aCoordenadasDeNave = (p: { x: number; y: number }, nave: NavePlanta) => ({
  x: p.x - nave.x,
  y: p.y - nave.y,
});

/** Racks y muelles se arrastran igual; sólo cambia a qué endpoint se guarda. */
export type Tipo = 'rack' | 'muelle' | 'camino' | 'sector' | 'nave';
/** Qué se dibuja al marcar un rectángulo sobre el plano, o al hacer clic sobre un muro. */
export type Dibujo = 'camino' | 'sector' | 'nave' | 'pared' | 'abertura' | 'rack';

/**
 * Un muro tal como lo necesita el editor para enganchar un clic: la clave
 * estable que arma el backend y sus dos puntos, en las mismas coordenadas
 * que el clic (del predio, o locales de una nave, según quien arme la lista).
 */
export type MuroPlano = { clave: string; a: Punto; b: Punto };

/**
 * Los cuatro lados de una nave, en coordenadas LOCALES —el rincón de la nave
 * es el origen— y en el mismo orden y sentido que `LADOS`/`murosPerimetrales`
 * de backend/src/domain/cerramientos.ts: norte y sur avanzan hacia +x, este y
 * oeste hacia +y. Tiene que coincidir, porque el offset de una abertura viaja
 * tal cual al servidor y ahí significa lo mismo. Quedan en local para que
 * quien las use decida cómo pasarlas al predio: el plano las traduce con el
 * origen de un arrastre en curso, y el enganche del clic con la posición
 * guardada nada más.
 */
export const LADOS = ['norte', 'este', 'sur', 'oeste'] as const;
export type Lado = (typeof LADOS)[number];

export function ladoDeNave(nave: { ancho: number; largo: number }, lado: Lado): { a: Punto; b: Punto } {
  const { ancho: an, largo: la } = nave;
  if (lado === 'norte') return { a: { x: 0, y: 0 }, b: { x: an, y: 0 } };
  if (lado === 'este') return { a: { x: an, y: 0 }, b: { x: an, y: la } };
  if (lado === 'sur') return { a: { x: 0, y: la }, b: { x: an, y: la } };
  return { a: { x: 0, y: 0 }, b: { x: 0, y: la } };
}

/**
 * La misma clave estable que arma `claveDeLado`/`claveDePared` en
 * backend/src/domain/cerramientos.ts: es lo que trae `AberturaPlanta.muro`,
 * y lo que hace falta para saber, mirando sólo la clave que devolvió
 * `muroMasCercano`, si una abertura va sobre una pared o sobre un lado.
 */
export const claveDeLado = (naveId: number, lado: Lado) => `n${naveId}-${lado}`;
export const claveDePared = (paredId: number) => `p${paredId}`;

/**
 * A qué muro engancha un clic, y a qué offset de su arranque cae. Proyecta el
 * punto sobre cada segmento —acotado a [0,1], la misma cuenta que
 * `offsetDelPunto` en backend/src/domain/cerramientos.ts— y se queda con el
 * de **menor distancia perpendicular**, no el primero que entra en
 * tolerancia: en una esquina, el primer muro que se recorra puede no ser el
 * que está más cerca de verdad. Devuelve `null` si ni el más cercano entra
 * dentro de la tolerancia.
 *
 * Está duplicada a propósito: son dos capas distintas (frontend y backend) y
 * compartir esta cuenta pediría un paquete común que este proyecto no tiene.
 */
export function muroMasCercano(
  p: Punto,
  muros: readonly MuroPlano[],
  tolerancia = 1,
): { muro: MuroPlano; offsetM: number } | null {
  let mejor: { muro: MuroPlano; offsetM: number; distanciaM: number } | null = null;

  for (const muro of muros) {
    const dx = muro.b.x - muro.a.x;
    const dy = muro.b.y - muro.a.y;
    const largo2 = dx * dx + dy * dy;
    const t =
      largo2 === 0 ? 0 : Math.min(1, Math.max(0, ((p.x - muro.a.x) * dx + (p.y - muro.a.y) * dy) / largo2));
    const proyX = muro.a.x + dx * t;
    const proyY = muro.a.y + dy * t;
    const distanciaM = Math.hypot(p.x - proyX, p.y - proyY);
    if (!mejor || distanciaM < mejor.distanciaM) {
      mejor = { muro, offsetM: t * Math.sqrt(largo2), distanciaM };
    }
  }

  if (!mejor || mejor.distanciaM > tolerancia) return null;
  return { muro: mejor.muro, offsetM: mejor.offsetM };
}
